/**
 * CONFIGURACIONES PARA CARGA MASIVA
 * 
 * Este archivo contiene las configuraciones específicas para cada módulo
 * que utiliza el componente CargaMasivaGenerico.
 * 
 * Cada configuración define:
 * - Estructura del Excel (headers, validaciones)
 * - Campos requeridos
 * - APIs a consumir
 * - Validaciones personalizadas
 * - Construcción de DTOs para enviar a la API
 * 
 * USO:
 * import { operadoresConfig, remolquesConfig, tractosConfig } from './configuraciones';
 * 
 * <CargaMasivaGenerico config={operadoresConfig(idTransportista)} />
 */

/**
 * CONFIGURACIÓN: OPERADORES
 * 
 * Permite carga masiva de operadores con sus unidades (tracto + remolque)
 * Soporta movimientos "full" (2 remolques)
 * 
 * @param {number} idTransportista - ID del transportista
 * @returns {Object} Configuración para CargaMasivaGenerico
 */
export const operadoresConfig = (idTransportista) => ({
  // Información básica
  title: "Carga Masiva de Operadores",
  subtitle: "Importa múltiples operadores desde un archivo Excel",
  templateName: "plantilla_operadores.xlsx",
  
  // Columnas del Excel
  headers: [
    'nombre', 'apellidoPaterno', 'apellidoMaterno', 'contacto', 
    'vigenciaLicencia', 'tipoLicencia'
  ],
  
  // Campos obligatorios
  requiredFields: [
    'nombre', 'contacto', 'vigenciaLicencia', 'tipoLicencia'
  ],
  
  // APIs a cargar para validaciones
  apiLoaders: [
    { url: `operadores/transportista/${idTransportista}`, cacheKey: 'operadores' }
  ],

  // Procesador de datos después de cargar APIs
  dataProcessor: (cache) => {
    return cache;
  },
  
  // Sin validaciones de selectores (solo campos de texto y fecha)
  validations: [],
  
  // Validaciones personalizadas
  customValidations: [
    {
      // Validar formato de la fecha de licencia
      validator: (row, index, allData, apiCache) => {
        if (!row.vigenciaLicencia || String(row.vigenciaLicencia).trim() === '') {
          return 'La fecha de vigencia de licencia es obligatoria';
        }
        
        const fechaRaw = row.vigenciaLicencia;
        
        // Si es número o string numérica (serial de Excel), es válido
        if (typeof fechaRaw === 'number') return null;
        const fechaStr = String(fechaRaw).trim();
        if (/^\d+$/.test(fechaStr)) return null;
        
        // Aceptar YYYY-MM-DD
        if (/^\d{4}-\d{2}-\d{2}$/.test(fechaStr)) return null;
        
        // Aceptar DD/MM/YYYY
        if (/^\d{2}\/\d{2}\/\d{4}$/.test(fechaStr)) return null;
        
        // Aceptar DD-MM-YYYY
        if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(fechaStr)) return null;
        
        // Aceptar cualquier cosa que Date pueda parsear
        const d = new Date(fechaStr);
        if (!isNaN(d.getTime())) return null;
        
        return 'La fecha de vigencia no es válida. Use formato DD-MM-YYYY (ej: 20-10-2026) o YYYY-MM-DD (ej: 2026-10-20)';
      }
    },
    {
      // Validar que el operador no exista previamente
      validator: (row, index, allData, apiCache) => {
        if (!Array.isArray(apiCache.operadores)) {
          return null;
        }
        
        const operadorExiste = apiCache.operadores.some(op => 
          op.nombre?.toLowerCase().trim() === row.nombre?.toLowerCase().trim() &&
          (op.paterno || '').toLowerCase().trim() === (row.apellidoPaterno || '').toLowerCase().trim() &&
          (op.materno || '').toLowerCase().trim() === (row.apellidoMaterno || '').toLowerCase().trim()
        );
        if (operadorExiste) {
          return `El operador ${row.nombre} ${row.apellidoPaterno || ''} ${row.apellidoMaterno || ''} ya existe para este transportista`;
        }
        return null;
      }
    },
    {
      // Validar duplicados dentro del mismo archivo
      validator: (row, index, allData) => {
        const duplicado = allData.find((r, i) => 
          i !== index && 
          r.nombre?.toLowerCase().trim() === row.nombre?.toLowerCase().trim() &&
          (r.apellidoPaterno || '').toLowerCase().trim() === (row.apellidoPaterno || '').toLowerCase().trim() &&
          (r.apellidoMaterno || '').toLowerCase().trim() === (row.apellidoMaterno || '').toLowerCase().trim()
        );
        if (duplicado) {
          return `El operador ${row.nombre} ${row.apellidoPaterno || ''} está duplicado en el archivo`;
        }
        return null;
      }
    }
  ],
  
  // Construir DTO para enviar a la API
  buildDTO: (row, apiCache) => {
    // Parsear fecha de vigencia a timestamp
    let fechaTimestamp = null;
    if (row.vigenciaLicencia) {
      const fechaRaw = row.vigenciaLicencia;
      const esNumero = typeof fechaRaw === 'number' || (typeof fechaRaw === 'string' && /^\d+$/.test(fechaRaw.trim()));
      
      if (esNumero) {
        // Serial de Excel: días desde 1900-01-01 (con bug de 1900 no bisiesto)
        const serial = typeof fechaRaw === 'number' ? fechaRaw : parseInt(fechaRaw.trim());
        const excelEpoch = new Date(1900, 0, 1);
        const d = new Date(excelEpoch.getTime() + (serial - 2) * 86400000);
        fechaTimestamp = d.getTime();
      } else {
        const fechaStr = String(fechaRaw).trim();
        // DD/MM/YYYY (con slashes)
        const ddmmyyyySlash = fechaStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        // DD-MM-YYYY (con guiones)
        const ddmmyyyyDash = fechaStr.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
        // YYYY-MM-DD
        const yyyymmdd = fechaStr.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
        
        if (ddmmyyyySlash) {
          fechaTimestamp = new Date(`${ddmmyyyySlash[3]}-${ddmmyyyySlash[2].padStart(2,'0')}-${ddmmyyyySlash[1].padStart(2,'0')}T12:00:00`).getTime();
        } else if (ddmmyyyyDash) {
          fechaTimestamp = new Date(`${ddmmyyyyDash[3]}-${ddmmyyyyDash[2].padStart(2,'0')}-${ddmmyyyyDash[1].padStart(2,'0')}T12:00:00`).getTime();
        } else if (yyyymmdd) {
          fechaTimestamp = new Date(`${fechaStr}T12:00:00`).getTime();
        } else {
          // Intentar parseo genérico
          const d = new Date(fechaStr);
          if (!isNaN(d.getTime())) {
            fechaTimestamp = d.getTime();
          }
        }
      }
    }

    return {
      nombre: (row.nombre || '').trim(),
      paterno: (row.apellidoPaterno || '').trim(),
      materno: (row.apellidoMaterno || '').trim(),
      medioComunicacion: (row.contacto || '').trim(),
      licenciaTipo: (row.tipoLicencia || '').trim(),
      licenciaVigencia: fechaTimestamp,
      idTransportista: idTransportista
    };
  },
  
  // Endpoint y método para guardar - ahora directamente en operadores
  apiEndpoint: "operadores",
  apiMethod: "post"
});

/**
 * CONFIGURACIÓN: REMOLQUES
 * 
 * Permite carga masiva de remolques
 * 
 * @param {number} idTransportista - ID del transportista
 * @returns {Object} Configuración para CargaMasivaGenerico
 */
export const remolquesConfig = (idTransportista) => ({
  title: "Carga Masiva de Remolques",
  subtitle: "Importa múltiples remolques desde un archivo Excel",
  templateName: "plantilla_remolques.xlsx",
  idTransportista: idTransportista,
  
  headers: ['placa', 'economico', 'tipoRemolque'],
  
  requiredFields: ['placa', 'economico', 'tipoRemolque'],
  
  apiLoaders: [
    { url: `remolques_transportistas/id_transportista/${idTransportista}`, cacheKey: 'relaciones' },
    { url: 'remolques', cacheKey: 'todosRemolques' },
    { url: (() => { const cu = JSON.parse(sessionStorage.getItem("currentUser")); const p = cu[0].perfil.id; return (p === 2925 || p === 4608) ? `remolquesTipo/perfil/${p}` : 'remolquesTipo'; })(), cacheKey: 'tiposRemolque' }
  ],
  
  validations: [
    {
      name: 'TiposRemolque',
      column: 'tipoRemolque',
      type: 'api',
      cacheKey: 'tiposRemolque',
      mapper: (data) => data.map(t => t.nombre)
    }
  ],
  
  customValidations: [
    {
      // Validar placas duplicadas en el archivo
      validator: (row, index, allData) => {
        if (!row.placa || row.placa.trim() === '') return null;
        
        const duplicado = allData.find((r, i) => 
          i !== index && r.placa?.toLowerCase().trim() === row.placa.toLowerCase().trim()
        );
        if (duplicado) {
          return `La placa ${row.placa} está duplicada en el archivo`;
        }
        return null;
      }
    },
    {
      // Validar que la placa no exista ya para este transportista
      validator: (row, index, allData, apiCache) => {
        if (!row.placa) return null;
        
        const relaciones = apiCache.relaciones || [];
        const todosRemolques = apiCache.todosRemolques || [];
        
        const idsRemolques = relaciones.map(r => r.idRemolque);
        const remolquesDelTransportista = todosRemolques.filter(r => idsRemolques.includes(r.idRemolque));
        
        const placaExiste = remolquesDelTransportista.some(r => 
          r.placa.toLowerCase().trim() === row.placa.toLowerCase().trim()
        );
        
        if (placaExiste) {
          return `La placa ${row.placa} ya existe para este transportista`;
        }
        return null;
      }
    },
    {
      // Validar que el tipo de remolque exista (solo si no está vacío)
      validator: (row, index, allData, apiCache) => {
        if (!row.tipoRemolque || row.tipoRemolque.trim() === '') return null;
        
        const tipos = apiCache.tiposRemolque || [];
        const tipoExiste = tipos.some(t => 
          t.nombre.toLowerCase().trim() === row.tipoRemolque.toLowerCase().trim()
        );
        if (!tipoExiste) {
          return `El tipo de remolque "${row.tipoRemolque}" no existe`;
        }
        return null;
      }
    }
  ],
  
  buildDTO: (row, apiCache) => {
    const tipos = apiCache.tiposRemolque || [];
    const tipo = tipos.find(t => t.nombre.toLowerCase() === row.tipoRemolque.toLowerCase());
    
    return {
      placa: (row.placa || '').toString().trim().toUpperCase(),
      economico: (row.economico || '').toString().trim(),
      idTransportista: idTransportista,
      remolquesTipos: {
        idRemolque: tipo?.idRemolque
      }
    };
  },
  
  apiEndpoint: "remolques",
  apiMethod: "post"
  
  // No necesita postProcess: el backend RemolquesService.crear() 
  // ya crea la relación remolques_transportistas internamente
});

/**
 * CONFIGURACIÓN: TRACTOS
 * 
 * Permite carga masiva de tractos (unidades tipo 1)
 * Incluye post-procesamiento para crear relación con transportista
 * 
 * @param {number} idTransportista - ID del transportista
 * @returns {Object} Configuración para CargaMasivaGenerico
 */
export const tractosConfig = (idTransportista) => ({
  title: "Carga Masiva de Tractos",
  subtitle: "Importa múltiples tractos desde un archivo Excel",
  templateName: "plantilla_tractos.xlsx",
  idTransportista: idTransportista, // Agregar para usar en post-procesamiento
  
  headers: ['placa', 'economico', 'marca', 'color', 'modelo'],
  
  requiredFields: ['placa', 'economico', 'marca', 'color', 'modelo'],
  
  apiLoaders: [
    { url: `transportistas_unidades_ter_nav/id_transportista/${idTransportista}`, cacheKey: 'relaciones' },
    { url: 'unidadTerNav', cacheKey: 'todosTractos' },
    { url: (() => { const cu = JSON.parse(sessionStorage.getItem("currentUser")); const p = cu[0].perfil.id; return (p === 2925 || p === 4608) ? `unidadesMarcasActivas/perfil/${p}` : 'unidadesMarcasActivas'; })(), cacheKey: 'marcas' },
    { url: 'colores', cacheKey: 'colores' }
  ],
  
  validations: [
    {
      name: 'Marcas',
      column: 'marca',
      type: 'api',
      cacheKey: 'marcas',
      mapper: (data) => Array.isArray(data) ? data.map(m => m.nombre) : []
    },
    {
      name: 'Colores',
      column: 'color',
      type: 'api',
      cacheKey: 'colores',
      mapper: (data) => Array.isArray(data) ? data.map(c => c.nombreColor) : []
    },
    {
      name: 'Modelos',
      column: 'modelo',
      type: 'static',
      // Generar años desde 1997 hasta el año actual
      options: Array.from({ length: new Date().getFullYear() - 1997 + 1 }, (_, i) => 
        (new Date().getFullYear() - i).toString()
      )
    }
  ],
  
  customValidations: [
    {
      // Validar placas duplicadas en el archivo
      validator: (row, index, allData) => {
        const duplicado = allData.find((r, i) => 
          i !== index && r.placa?.toLowerCase() === row.placa?.toLowerCase()
        );
        if (duplicado) {
          return `La placa ${row.placa} está duplicada en el archivo`;
        }
        return null;
      }
    },
    {
      // Validar que la placa no exista ya para este transportista
      validator: (row, index, allData, apiCache) => {
        if (!row.placa) return null;
        
        const relaciones = apiCache.relaciones || [];
        const todosTractos = apiCache.todosTractos || [];
        
        const idsUnidades = relaciones.map(r => r.idUnidad);
        const tractosDelTransportista = todosTractos.filter(t => idsUnidades.includes(t.id));
        
        const placaExiste = tractosDelTransportista.some(t => 
          t.placa.toLowerCase().trim() === row.placa.toLowerCase().trim()
        );
        
        if (placaExiste) {
          return `La placa ${row.placa} ya existe para este transportista`;
        }
        return null;
      }
    },
    {
      // Validar que la marca exista (solo si no está vacía)
      validator: (row, index, allData, apiCache) => {
        if (!row.marca || row.marca.trim() === '') return null;
        
        const marcas = apiCache.marcas || [];
        const marcaExiste = marcas.some(m => 
          m.nombre.toLowerCase().trim() === row.marca.toLowerCase().trim()
        );
        if (!marcaExiste) {
          return `La marca "${row.marca}" no existe en el catálogo`;
        }
        return null;
      }
    },
    {
      // Validar que el color exista (solo si no está vacío)
      validator: (row, index, allData, apiCache) => {
        if (!row.color || row.color.trim() === '') return null;
        
        const colores = apiCache.colores || [];
        const colorExiste = colores.some(c => 
          c.nombreColor.toLowerCase().trim() === row.color.toLowerCase().trim()
        );
        if (!colorExiste) {
          return `El color "${row.color}" no existe en el catálogo`;
        }
        return null;
      }
    }
  ],
  
  buildDTO: (row, apiCache) => {
    const marcas = apiCache.marcas || [];
    const colores = apiCache.colores || [];
    
    const marca = marcas.find(m => 
      m.nombre && row.marca && 
      m.nombre.toLowerCase().trim() === row.marca.toLowerCase().trim()
    );
    const color = colores.find(c => 
      c.nombreColor && row.color && 
      c.nombreColor.toLowerCase().trim() === row.color.toLowerCase().trim()
    );
    
    console.log('🔍 Objetos encontrados:', {
      marca: marca,
      color: color,
      marcaId: marca?.id,
      colorId: color?.idColor
    });
    
    const dto = {
      placa: (row.placa || '').toString().trim().toUpperCase(),
      economico: (row.economico || '').toString().trim(),
      color: color?.nombreColor || row.color || 'Sin especificar',
      modelo: row.modelo ? row.modelo.toString() : new Date().getFullYear().toString(),
      idUnidadTipo: {
        id: 28  // Tipo 28 = Tracto
      },
      idUnidadEstatusOperacion: {
        id: 1  // Estatus operación activo
      },
      estatus: true
    };
    
    // Verificar estructura de marca
    if (marca) {
      dto.idUnidadMarca = {
        id: parseInt(marca.id)
      };
    }
    
    // Verificar estructura de color
    if (color && color.idColor) {
      dto.idColor = {
        idColor: parseInt(color.idColor)
      };
    }
    
    return dto;
  },
  
  apiEndpoint: "unidadTerNav",
  apiMethod: "post",
  
  /**
   * Guardado personalizado: Maneja el caso donde la placa ya existe.
   * 1. Intenta crear el tracto
   * 2. Si la placa ya existe, busca el tracto existente
   * 3. Crea la relación transportista-tracto (si no existe)
   */
  customSave: async (row, apiCache, idTransportista) => {
    const { Crud_Catalogos } = await import('../../herramientas/Crud_Catalogos');
    
    const marcas = apiCache.marcas || [];
    const colores = apiCache.colores || [];
    const marca = marcas.find(m => m.nombre?.toLowerCase().trim() === row.marca?.toLowerCase().trim());
    const color = colores.find(c => c.nombreColor?.toLowerCase().trim() === row.color?.toLowerCase().trim());
    
    const dto = {
      placa: (row.placa || '').toString().trim().toUpperCase(),
      economico: (row.economico || '').toString().trim(),
      color: color?.nombreColor || row.color || 'Sin especificar',
      modelo: row.modelo ? row.modelo.toString() : new Date().getFullYear().toString(),
      idUnidadTipo: { id: 28 },
      idUnidadEstatusOperacion: { id: 1 },
      estatus: true
    };
    if (marca) dto.idUnidadMarca = { id: parseInt(marca.id) };
    if (color?.idColor) dto.idColor = { idColor: parseInt(color.idColor) };
    
    let tractoId;
    
    try {
      // Intentar crear el tracto (stateElementos="" para que retorne res.data)
      const result = await Crud_Catalogos("unidadTerNav", "", "post", "", dto, "", "", "", []);
      tractoId = result?.id;
    } catch (error) {
      // Si la placa ya existe, buscar el tracto existente
      if (error.response?.status === 400) {
        const todos = await Crud_Catalogos("unidadTerNav", "", "get", "", []);
        const existente = todos.find(t => t.placa?.toUpperCase().trim() === dto.placa);
        if (existente) {
          tractoId = existente.id;
        } else {
          throw error;
        }
      } else {
        throw error;
      }
    }
    
    // Crear relación con transportista (solo si no existe ya)
    if (idTransportista && tractoId) {
      const relaciones = await Crud_Catalogos(
        "transportistas_unidades_ter_nav/id_unidad/" + tractoId, "", "get", "", []
      );
      const yaRelacionado = Array.isArray(relaciones) && relaciones.some(
        r => r.idTransportista === idTransportista
      );
      if (!yaRelacionado) {
        await Crud_Catalogos("transportistas_unidades_ter_nav", "", "post", "", {
          idUnidad: tractoId,
          idTransportista: idTransportista
        }, "", "", "", []);
      }
    }
    
    return { id: tractoId, placa: dto.placa };
  }
});
